/**
 * 認証・編集権限コンテキスト
 *
 * `auth.me` の結果 (email / canEdit / canManageEditors) をアプリ全体へ供給する。
 * 未取得・未ログイン時は権限なしの既定値を返す。
 *
 * @graph-stack creative
 * @graph-domain Marketing
 * @graph-business auth.me の結果 (編集権限フラグ) をアプリ全体へ供給する React コンテキスト。
 * @graph-connects api [calls] auth.me による認証状態取得
 */
import type { CreativeAuthUser } from '@cortex/domain-creative/web';
import { useQuery } from '@tanstack/react-query';
import { createContext, useContext, type JSX, type ReactNode } from 'react';
import { orpc } from '../api/orpc.js';

/** 未ログイン時の既定値。 @graph-connects none */
const ANONYMOUS: CreativeAuthUser = {
  email: '',
  name: null,
  canEdit: false,
  canManageEditors: false,
};

/** @graph-connects none */
const AuthContext = createContext<CreativeAuthUser>(ANONYMOUS);

/**
 * 認証コンテキストプロバイダ。`auth.me` を 1 回取得して配下へ供給する。
 * @param props children
 * @returns プロバイダ要素
 * @graph-connects api [calls] auth.me による認証状態取得
 */
export function AuthProvider(props: { children: ReactNode }): JSX.Element {
  const meQuery = useQuery(orpc.auth.me.queryOptions({ input: {}, retry: false }));
  return (
    <AuthContext.Provider value={meQuery.data ?? ANONYMOUS}>{props.children}</AuthContext.Provider>
  );
}

/**
 * 現在のログインユーザーと権限を取得するフック。
 * @returns 認証コンテキスト値
 * @graph-connects none
 */
export function useAuth(): CreativeAuthUser {
  return useContext(AuthContext);
}
